/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import styles from './CategorySidebar.module.scss';

import { CategoryNavItem } from './CategorySidebar.types';

type Props = {
  items: CategoryNavItem[];
  totalLabel: string;
};

export const CategorySidebar = ({ items, totalLabel }: Props) => {
  const { t } = useInt();
  const [activeId, setActiveId] = useState(items[0]?.id);

  useEffect(() => {
    const panels = items.map((item) => document.getElementById(item.id)).filter((el): el is HTMLElement => !!el);

    if (!panels.length || !('IntersectionObserver' in window)) {
      return undefined;
    }

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.add(entry.target.id);
          } else {
            visible.delete(entry.target.id);
          }
        });

        const current = panels.find((panel) => visible.has(panel.id));
        if (current) {
          setActiveId(current.id);
        }
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );

    panels.forEach((panel) => observer.observe(panel));

    return () => observer.disconnect();
  }, [items]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  return (
    <nav className={styles.root} aria-label="Data categories">
      <p className={styles.label}>{t('Data categories')}</p>

      <div className={styles.list}>
        {items.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={cn(styles.item, activeId === item.id && styles.itemActive)}
            aria-current={activeId === item.id}
            onClick={(e) => handleClick(e, item.id)}
          >
            <span>{item.label}</span>
            <span className={styles.itemCount}>{item.count}</span>
          </a>
        ))}
      </div>

      <p className={styles.total}>{totalLabel}</p>

      <a
        className={styles.cta}
        href="#how-to-access-the-data"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('how-to-access-the-data')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {t('How to access the data')}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h13" />
          <path d="M13 6l6 6-6 6" />
        </svg>
      </a>
    </nav>
  );
};
